import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BlogApi } from "../../service";
import APPLICATION_SETTINGS from "../../settings";
import useApiHandler from "../hooks/ApiHandler";

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { callApi, loading } = useApiHandler();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  const fetchPost = async () => {
    setError(null);
    try {
      await callApi(() => BlogApi.getPostById(slug), {
        onSuccess: (response) => {
          setPost(response?.data);
        },
        onError: (err) => {
          if (err?.response?.status === 404) {
            setError("Article not found.");
          } else if (err?.response?.status === 401 || err?.response?.status === 403) {
            setError("You must be logged in to view this article.");
          } else {
            setError("Unable to load this article. Please try again later.");
          }
        },
      });
    } catch (err) {
      // handled by onError
    }
  };

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const buildImageUrl = (path) => {
    if (!path) return null;
    try {
      return new URL(path, APPLICATION_SETTINGS.VITE_API_BASE).toString();
    } catch (error) {
      return path;
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <button
          className="text-[#3CAB5C] font-semibold mb-4"
          onClick={() => navigate(-1)}
        >
          ← Back to articles
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (loading || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-[#595959]">
        Loading article...
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 py-12">
      <button
        className="text-[#3CAB5C] font-semibold mb-6"
        onClick={() => navigate(-1)}
      >
        ← Back to articles
      </button>

      <p className="text-xs text-[#3CAB5C] font-semibold uppercase tracking-[0.3em] mb-3">
        {formatDate(post.publishedAt)}
      </p>
      <h1 className="text-3xl md:text-4xl font-semibold text-[#242424] mb-4">
        {post.title}
      </h1>
      <p className="text-sm text-[#595959] mb-6">
        By {post.authorName || "Properteasy Team"}
      </p>

      {post.heroImage && (
        <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
          <img
            src={buildImageUrl(post.heroImage)}
            alt={post.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      <div
        className="blog-content"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
    </article>
  );
};

export default BlogDetailPage;
